import React, { useEffect, useState, useMemo } from 'react';
import { SafetyReport, IngestionReportCategory, ReportAssessmentPayload } from './types';
import { ingestionService } from './ingestionService';
import { taxonomyService } from '../../shared/taxonomyService';
import { occurrenceService } from '../safety/occurrences/occurrenceService';
import { Occurrence } from '../safety/occurrences/types';
import { investigationService } from '../safety/investigations/investigationService';
import { Investigation } from '../safety/investigations/types';
import {
  Mail,
  X,
  Check,
  Link2,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Search,
  FileText,
  Paperclip,
  Clock,
  Calendar,
  Plane,
  MapPin,
  ShieldAlert,
  Send,
  Save,
  Trash2,
  XCircle,
} from 'lucide-react';

interface AssessReportPageProps {
  reportId?: string;
  onBack?: () => void;
}

const TARGET_DEPARTMENTS = [
  'Flight Operations',
  'Technical & Part-145 Maintenance',
  'Ground Handling & Ramp Operations',
  'Cabin Operations & Service',
  'Corporate & Aviation Security (AVSEC)',
  'Quality Assurance (QMS)',
  'Safety & Quality (SMS Central)',
  'Compliance Monitoring',
  'CAMO / Continuing Airworthiness',
  'Training Academy',
  'Administration & Facilities',
];

export const AssessReportPage: React.FC<AssessReportPageProps> = ({ reportId: propReportId, onBack }) => {
  // Determine report ID from prop or URL
  const reportId = useMemo(() => {
    if (propReportId) return propReportId;
    const params = new URLSearchParams(window.location.search);
    return params.get('id') || params.get('reportId') || '';
  }, [propReportId]);

  const [report, setReport] = useState<SafetyReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Mail to Reporter state
  const [showMailModal, setShowMailModal] = useState<boolean>(false);
  const [mailRecipient, setMailRecipient] = useState<string>('');
  const [mailSubject, setMailSubject] = useState<string>('');
  const [mailMessage, setMailMessage] = useState<string>('');
  const [isSendingMail, setIsSendingMail] = useState<boolean>(false);
  const [mailStatusMsg, setMailStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Middle tab state: 'occurrence' (Occurrence Information) vs 'attachments'
  const [middleTab, setMiddleTab] = useState<'occurrence' | 'attachments'>('occurrence');

  // Taxonomy states
  const [reviewAreaOfOccurrence, setReviewAreaOfOccurrence] = useState<string>('Air Safety Report (ASR)');
  const [reviewTypeOfOccurrence, setReviewTypeOfOccurrence] = useState<string>('ATM/CNS');
  const [reviewEventClassification, setReviewEventClassification] = useState<string>('');
  const [reviewInitialSeverity, setReviewInitialSeverity] = useState<string>('MEDIUM');

  // Delete dialog
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteReason, setDeleteReason] = useState<string>('');

  // Reject dialog
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');

  // Outcome selection: INFO_ONLY vs CORRECTIVE_ACTION vs INVESTIGATION
  const [assessmentOutcome, setAssessmentOutcome] = useState<'INFO_ONLY' | 'CORRECTIVE_ACTION' | 'INVESTIGATION'>('INFO_ONLY');
  const [assessmentNotes, setAssessmentNotes] = useState<string>('');

  // Linkage states: Info Only
  const [infoLinkMode, setInfoLinkMode] = useState<'CREATE_NEW' | 'LINK_EXISTING'>('CREATE_NEW');
  const [existingInfoReports, setExistingInfoReports] = useState<any[]>([]);
  const [selectedInfoReferenceNumber, setSelectedInfoReferenceNumber] = useState<string>('');
  const [infoSearchTerm, setInfoSearchTerm] = useState<string>('');

  // Linkage states: Occurrence (OCR-)
  const [occurrenceLinkMode, setOccurrenceLinkMode] = useState<'CREATE_NEW' | 'LINK_EXISTING'>('CREATE_NEW');
  const [existingOccurrences, setExistingOccurrences] = useState<Occurrence[]>([]);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string>('');
  const [selectedOccurrenceNumber, setSelectedOccurrenceNumber] = useState<string>('');
  const [occurrenceSearchTerm, setOccurrenceSearchTerm] = useState<string>('');

  // Linkage states: Investigation (INV-)
  const [investigationLinkMode, setInvestigationLinkMode] = useState<'CREATE_NEW' | 'LINK_EXISTING'>('CREATE_NEW');
  const [existingInvestigations, setExistingInvestigations] = useState<Investigation[]>([]);
  const [selectedInvestigationId, setSelectedInvestigationId] = useState<string>('');
  const [selectedInvestigationNumber, setSelectedInvestigationNumber] = useState<string>('');
  const [investigationSearchTerm, setInvestigationSearchTerm] = useState<string>('');

  // Corrective Action fields
  const [actionCustomLabel, setActionCustomLabel] = useState<string>('Corrective Action (CAPA)');
  const [actionTitle, setActionTitle] = useState<string>('');
  const [actionType, setActionType] = useState<'CORRECTIVE' | 'PREVENTIVE' | 'CONTAINMENT'>('CORRECTIVE');
  const [actionDepartment, setActionDepartment] = useState<string>('Flight Operations');
  const [actionAssignee, setActionAssignee] = useState<string>('');
  const [actionDueDate, setActionDueDate] = useState<string>('');
  const [actionPriority, setActionPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'IMMEDIATE'>('MEDIUM');

  // Investigation fields
  const [investigationTitle, setInvestigationTitle] = useState<string>('');
  const [investigationType, setInvestigationType] = useState<'FORMAL' | 'COMPREHENSIVE' | 'PRELIMINARY' | 'DESK' | 'SPECIAL'>('FORMAL');
  const [investigationPriority, setInvestigationPriority] = useState<'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'>('MEDIUM');
  const [leadInvestigator, setLeadInvestigator] = useState<string>('');
  const [targetCompletionDate, setTargetCompletionDate] = useState<string>('');
  const [investigationSummary, setInvestigationSummary] = useState<string>('');

  // Taxonomy options
  const taxonomyAreas = useMemo(() => taxonomyService.getAllAreaNames(), []);
  const taxonomyTypes = useMemo(() => {
    if (reviewAreaOfOccurrence) {
      const types = taxonomyService.getTypesForArea(reviewAreaOfOccurrence);
      if (types.length > 0) return types;
    }
    return taxonomyService.getAllTypeNames();
  }, [reviewAreaOfOccurrence]);
  const taxonomyClassifications = useMemo(() => {
    const list = taxonomyService.getClassificationsForAreaAndType(reviewAreaOfOccurrence, reviewTypeOfOccurrence);
    if (list.length > 0) return list;
    return taxonomyService.getAllClassificationNames();
  }, [reviewAreaOfOccurrence, reviewTypeOfOccurrence]);

  // Filtered linkable lists
  const filteredOccurrences = useMemo(() => {
    if (!occurrenceSearchTerm.trim()) return existingOccurrences;
    const raw = occurrenceSearchTerm.trim();
    // If user typed just "ocr", "occ", "ocr-", "occ-", don't filter out by prefix alone
    if (/^(ocr|occ)-?$/i.test(raw)) {
      return existingOccurrences;
    }

    const term = raw.toLowerCase();
    const occTerm = term.replace(/^ocr/i, 'occ');
    const ocrTerm = term.replace(/^occ/i, 'ocr');
    const numOnly = term.replace(/[^0-9]/g, '');

    return existingOccurrences.filter((o) => {
      const rawNum = (o.occurrenceNumber || '').toLowerCase();
      const canonicalNum = rawNum.replace(/^occ-/, 'ocr-');
      const title = (o.title || '').toLowerCase();
      const flight = (o.flightNumber || '').toLowerCase();

      const matchesNum =
        rawNum.includes(term) ||
        rawNum.includes(occTerm) ||
        rawNum.includes(ocrTerm) ||
        canonicalNum.includes(term);
      const matchesNumOnly = numOnly.length >= 2 && (rawNum.includes(numOnly) || canonicalNum.includes(numOnly));
      const matchesTitle = title.includes(term);
      const matchesFlight = flight.includes(term);

      return matchesNum || matchesNumOnly || matchesTitle || matchesFlight;
    });
  }, [existingOccurrences, occurrenceSearchTerm]);

  const selectedOccurrence = useMemo(() => {
    return existingOccurrences.find((o) => o.id === selectedOccurrenceId);
  }, [existingOccurrences, selectedOccurrenceId]);

  const filteredInvestigations = useMemo(() => {
    if (!investigationSearchTerm.trim()) return existingInvestigations;
    const term = investigationSearchTerm.toLowerCase();
    return existingInvestigations.filter(
      (i) =>
        (i.investigationNumber && i.investigationNumber.toLowerCase().includes(term)) ||
        (i.title && i.title.toLowerCase().includes(term))
    );
  }, [existingInvestigations, investigationSearchTerm]);

  const filteredInfoReports = useMemo(() => {
    if (!infoSearchTerm.trim()) return existingInfoReports;
    const term = infoSearchTerm.toLowerCase();
    return existingInfoReports.filter(
      (r) =>
        (r.info_reference_number && r.info_reference_number.toLowerCase().includes(term)) ||
        (r.report_number && r.report_number.toLowerCase().includes(term)) ||
        (r.title && r.title.toLowerCase().includes(term))
    );
  }, [existingInfoReports, infoSearchTerm]);

  // Dynamic server-side search for occurrences when searching
  useEffect(() => {
    if (!occurrenceSearchTerm.trim()) return;
    const timer = setTimeout(() => {
      occurrenceService
        .getOccurrences({
          forLinkage: true,
          limit: 100,
          searchTerm: occurrenceSearchTerm.trim(),
        })
        .then((res) => {
          if (res.items && res.items.length > 0) {
            setExistingOccurrences((prev) => {
              const map = new Map<string, Occurrence>();
              prev.forEach((o) => map.set(o.id, o));
              res.items.forEach((o) => map.set(o.id, o));
              return Array.from(map.values());
            });
          }
        })
        .catch(console.error);
    }, 300);
    return () => clearTimeout(timer);
  }, [occurrenceSearchTerm]);

  // Dynamic server-side search for investigations when searching
  useEffect(() => {
    if (!investigationSearchTerm.trim()) return;
    const timer = setTimeout(() => {
      investigationService
        .getInvestigations({
          forLinkage: true,
          limit: 100,
          searchTerm: investigationSearchTerm.trim(),
        })
        .then((res) => {
          if (res.items && res.items.length > 0) {
            setExistingInvestigations((prev) => {
              const map = new Map<string, Investigation>();
              prev.forEach((i) => map.set(i.id, i));
              res.items.forEach((i) => map.set(i.id, i));
              return Array.from(map.values());
            });
          }
        })
        .catch(console.error);
    }, 300);
    return () => clearTimeout(timer);
  }, [investigationSearchTerm]);

  // Load report data
  useEffect(() => {
    if (!reportId) {
      setErrorMsg('No report ID specified.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMsg(null);

    // Fetch linkable records in background (bypassing personal scope so all organizational occurrences are linkable)
    occurrenceService.getOccurrences({ forLinkage: true, limit: 500 }).then((res) => {
      setExistingOccurrences(res.items || []);
    }).catch(console.error);

    investigationService.getInvestigations({ forLinkage: true, limit: 500 }).then((res) => {
      setExistingInvestigations(res.items || []);
    }).catch(console.error);

    ingestionService.getLinkableInfoReports().then((res) => {
      setExistingInfoReports(res || []);
    }).catch(console.error);

    ingestionService
      .getReportById(reportId)
      .then((data) => {
        if (!data) {
          setErrorMsg(`Report #${reportId} was not found.`);
          return;
        }
        setReport(data);

        // Initialize taxonomy
        const defaultArea = taxonomyService.getAllAreaNames()[0] || 'Air Safety Report (ASR)';
        const chosenArea = data.area_of_occurrence || defaultArea;
        setReviewAreaOfOccurrence(chosenArea);

        const availableTypes = taxonomyService.getTypesForArea(chosenArea);
        const defaultType = availableTypes[0] || taxonomyService.getAllTypeNames()[0] || '';
        const chosenType = data.type_of_occurrence || defaultType;
        setReviewTypeOfOccurrence(chosenType);

        const availableClasses = taxonomyService.getClassificationsForAreaAndType(chosenArea, chosenType);
        const defaultClass = availableClasses[0] || taxonomyService.getAllClassificationNames()[0] || '';
        setReviewEventClassification(data.event_classification || defaultClass);

        setReviewInitialSeverity(data.initial_severity || 'MEDIUM');
        setAssessmentNotes(data.assessment_notes || data.validation_notes || '');

        // Outcome default
        const initOutcome = data.assessment_outcome || (data.initial_severity === 'CRITICAL' ? 'INVESTIGATION' : data.corrective_actions_required ? 'CORRECTIVE_ACTION' : 'INFO_ONLY');
        setAssessmentOutcome(initOutcome);

        // Action defaults
        setActionTitle(`Corrective Action: [${data.report_number}] ${data.title}`);
        setActionType('CORRECTIVE');
        setActionCustomLabel('Corrective Action (CAPA)');
        setActionDepartment(data.responsible_department || data.assigned_department || 'Flight Operations');
        setActionAssignee('');
        const plus14 = new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString().substring(0, 10);
        setActionDueDate(data.action_due_date ? data.action_due_date.substring(0, 10) : plus14);
        setActionPriority(data.initial_severity === 'CRITICAL' ? 'IMMEDIATE' : data.initial_severity === 'HIGH' ? 'HIGH' : 'MEDIUM');

        // Investigation defaults
        setInvestigationTitle(`[${data.report_number}] ${data.title}`);
        setInvestigationType('FORMAL');
        setInvestigationPriority(data.initial_severity === 'CRITICAL' ? 'CRITICAL' : data.initial_severity === 'HIGH' ? 'HIGH' : 'MEDIUM');
        setLeadInvestigator('');
        const plus30 = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().substring(0, 10);
        setTargetCompletionDate(plus30);
        setInvestigationSummary(data.description || data.title);
      })
      .catch((err) => {
        setErrorMsg(err?.message || 'Failed to load report for assessment.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [reportId]);

  const handleOpenMailModal = () => {
    if (!report) return;
    setMailRecipient(report.reporter_identity?.email || '');
    setMailSubject(`[SafetyCore SMS] Feedback on Safety Report #${report.report_number} - ${report.title}`);
    setMailMessage('');
    setMailStatusMsg(null);
    setShowMailModal(true);
  };

  const handleApplyMailTemplate = (type: 'DETAILS' | 'ASSESSMENT' | 'ACTION') => {
    if (!report) return;
    if (type === 'DETAILS') {
      setMailSubject(`[SafetyCore SMS] Request for Information: Report #${report.report_number}`);
      setMailMessage(
        `Dear Reporter,\n\nThank you for submitting safety report #${report.report_number} regarding "${report.title}".\n\nIn order to complete the safety assessment and determine appropriate corrective actions, could you please provide additional details regarding the event sequence, operating conditions, and any immediate actions taken?\n\nThank you for supporting our organization's safety standards.\n\nSincerely,\nSafety Management System (SMS) Desk`
      );
    } else if (type === 'ASSESSMENT') {
      setMailSubject(`[SafetyCore SMS] Status Update: Safety Report #${report.report_number}`);
      setMailMessage(
        `Dear Reporter,\n\nWe would like to inform you that your safety report #${report.report_number} ("${report.title}") has been received and is currently under active safety risk assessment by the SMS Safety Desk.\n\nYou will receive further updates as the assessment progresses.\n\nThank you for your active participation in our reporting system.\n\nSafety & Quality Operations`
      );
    } else if (type === 'ACTION') {
      setMailSubject(`[SafetyCore SMS] Occurrence & Action Update: Report #${report.report_number}`);
      setMailMessage(
        `Dear Reporter,\n\nYour safety report #${report.report_number} ("${report.title}") has been assessed by the Safety Desk. An Occurrence record has been opened and corrective/preventive actions are being assigned to the responsible operational department.\n\nWe appreciate your vigilance and dedication to aviation safety.\n\nSafety Management System Team`
      );
    }
  };

  const handleSendMailToReporter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!report) return;
    if (!mailRecipient.trim()) {
      setMailStatusMsg({ type: 'error', text: 'Please enter a valid recipient email address.' });
      return;
    }
    if (!mailMessage.trim()) {
      setMailStatusMsg({ type: 'error', text: 'Message body cannot be empty.' });
      return;
    }

    try {
      setIsSendingMail(true);
      setMailStatusMsg(null);
      const res = await ingestionService.mailToReporter(report.id, {
        recipientEmail: mailRecipient.trim(),
        subject: mailSubject.trim(),
        message: mailMessage.trim(),
      });
      setMailStatusMsg({
        type: 'success',
        text: `✓ Email successfully sent to ${res.sentTo} and logged in the report audit trail!`,
      });
      setTimeout(() => {
        setShowMailModal(false);
      }, 1600);
    } catch (err: any) {
      setMailStatusMsg({
        type: 'error',
        text: err?.message || 'Failed to dispatch email to reporter.',
      });
    } finally {
      setIsSendingMail(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      const newUrl = '?tab=ingestion&subTab=waiting_assessment';
      window.history.pushState({ tab: 'ingestion' }, '', newUrl);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const handleSaveAssessment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!report) return;

    if (assessmentOutcome === 'CORRECTIVE_ACTION') {
      if (occurrenceLinkMode === 'LINK_EXISTING') {
        if (!selectedOccurrenceId) {
          alert('Please select an existing Occurrence to link this report to.');
          return;
        }
        if (!selectedOccurrence?.correctiveActionNumber) {
          if (!actionTitle.trim()) {
            alert('Please specify an Action Title for the Corrective Action.');
            return;
          }
          if (!actionDepartment) {
            alert('Please select a Responsible Department for the Corrective Action.');
            return;
          }
        }
      } else {
        if (!actionTitle.trim()) {
          alert('Please specify an Action Title for the Corrective Action.');
          return;
        }
        if (!actionDepartment) {
          alert('Please select a Responsible Department for the Corrective Action.');
          return;
        }
      }
    }

    if (assessmentOutcome === 'INVESTIGATION') {
      if (investigationLinkMode === 'LINK_EXISTING' && !selectedInvestigationId) {
        alert('Please select an existing Safety Investigation to link this report to.');
        return;
      }
      if (investigationLinkMode === 'CREATE_NEW') {
        if (!investigationTitle.trim()) {
          alert('Please enter an Investigation Title.');
          return;
        }
      }
    }

    if (assessmentOutcome === 'INFO_ONLY') {
      if (infoLinkMode === 'LINK_EXISTING' && !selectedInfoReferenceNumber) {
        alert('Please select an existing Info Only reference or report.');
        return;
      }
    }

    try {
      setIsSaving(true);
      const payload: ReportAssessmentPayload = {
        assessment_outcome: assessmentOutcome,
        assessment_notes: assessmentNotes,
        area_of_occurrence: reviewAreaOfOccurrence,
        type_of_occurrence: reviewTypeOfOccurrence,
        event_classification: reviewEventClassification,
        initial_severity: reviewInitialSeverity,
        // Link Mode and References
        link_mode:
          assessmentOutcome === 'INFO_ONLY'
            ? infoLinkMode
            : assessmentOutcome === 'INVESTIGATION'
            ? investigationLinkMode
            : occurrenceLinkMode,
        existing_occurrence_id: selectedOccurrenceId || undefined,
        existing_occurrence_number: selectedOccurrenceNumber || undefined,
        existing_investigation_id: selectedInvestigationId || undefined,
        existing_investigation_number: selectedInvestigationNumber || undefined,
        existing_info_reference_number: selectedInfoReferenceNumber || undefined,
        // Corrective Action fields
        action_title: actionTitle,
        action_type: actionType,
        action_custom_label: actionCustomLabel,
        responsible_department: actionDepartment,
        assignee_name: actionAssignee,
        action_due_date: actionDueDate,
        action_priority: actionPriority,
        // Investigation fields
        investigation_title: investigationTitle,
        investigation_type: investigationType,
        investigation_priority: investigationPriority,
        lead_investigator_name: leadInvestigator,
        target_completion_date: targetCompletionDate,
        investigation_summary: investigationSummary,
      };

      const res = await ingestionService.assessReport(report.id, payload);

      if (assessmentOutcome === 'INVESTIGATION') {
        if (investigationLinkMode === 'LINK_EXISTING') {
          alert(`🔍 Report #${report.report_number} successfully linked to Safety Investigation #${selectedInvestigationNumber || res.investigation?.investigationNumber}!\n\nYou can view and manage this investigation in Safety Investigations.`);
        } else {
          alert(`🔍 Safety Investigation initiated successfully!\n\nCase Number: ${res.investigation?.investigationNumber}\nStatus: INITIATED\nLinked Report: ${report.report_number}\n\nYou can view and manage this investigation in Safety Investigations.`);
        }
      } else if (assessmentOutcome === 'CORRECTIVE_ACTION') {
        if (occurrenceLinkMode === 'LINK_EXISTING') {
          alert(`⚡ Report #${report.report_number} successfully linked to Occurrence #${selectedOccurrenceNumber || res.occurrence?.occurrenceNumber}!\n\nShared CAPA Action: ${res.action?.actionNumber || 'N/A'}\nResponsible Department: ${res.action?.department || 'N/A'}\nDue Date: ${res.action?.dueDate || 'N/A'}\n\nYou can view the linked occurrence in Occurrences.`);
        } else {
          alert(`⚡ Occurrence created and report linked successfully!\n\nOccurrence Number: ${res.occurrence?.occurrenceNumber}\nCAPA Action Number: ${res.action?.actionNumber}\nDepartment: ${res.action?.department}\nDue Date: ${res.action?.dueDate}\n\nYou can view and track this event in Occurrences.`);
        }
      } else {
        if (infoLinkMode === 'LINK_EXISTING') {
          alert(`✓ Report #${report.report_number} linked to existing Info Reference #${selectedInfoReferenceNumber} and filed.`);
        } else {
          alert(`✓ Report #${report.report_number} successfully assessed as Info Only.\n\nGenerated Reference: ${res.infoReferenceNumber || 'INF Generated'}\nStatus: CLOSED`);
        }
      }

      handleBack();
    } catch (err: any) {
      alert(`Assessment save failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveValidationDraft = async () => {
    if (!report) return;
    try {
      setIsSaving(true);
      await ingestionService.saveValidationDraft(report.id, {
        area_of_occurrence: reviewAreaOfOccurrence,
        type_of_occurrence: reviewTypeOfOccurrence,
        event_classification: reviewEventClassification,
        initial_severity: reviewInitialSeverity,
        assigned_department: actionDepartment || report.assigned_department,
        validation_notes: assessmentNotes,
        corrective_actions_required: assessmentOutcome === 'CORRECTIVE_ACTION',
        investigation_required: assessmentOutcome === 'INVESTIGATION',
      });
      alert('✓ Assessment draft saved successfully.');
    } catch (err: any) {
      alert(`Failed to save draft: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!report) return;
    const cleanReason = (deleteReason || '').trim();
    if (cleanReason.length < 3) {
      alert('Please enter a valid reason for deletion (at least 3 characters).');
      return;
    }
    try {
      setIsSaving(true);
      await ingestionService.deleteReportWithReason(report.id, cleanReason);
      setShowDeleteModal(false);
      alert(`Report #${report.report_number} was soft-deleted and permanently recorded in the audit trail.`);
      handleBack();
    } catch (err: any) {
      alert(`Delete failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!report) return;
    if (!rejectReason.trim()) {
      alert('Please enter a rejection reason.');
      return;
    }
    try {
      setIsSaving(true);
      await ingestionService.rejectValidation(report.id, {
        reason: rejectReason.trim(),
      });
      setShowRejectModal(false);
      alert(`Report #${report.report_number} has been rejected.`);
      handleBack();
    } catch (err: any) {
      alert(`Rejection failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
        <div style={{ fontSize: '36px', marginBottom: '12px' }}>⏳</div>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Loading Report Assessment Desk...</div>
        <div style={{ fontSize: '13px', marginTop: '4px' }}>Fetching occurrence details and taxonomy mapping.</div>
      </div>
    );
  }

  if (errorMsg || !report) {
    return (
      <div style={{ maxWidth: '800px', margin: '40px auto', padding: '24px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#991b1b', marginBottom: '8px' }}>
          <AlertTriangle size={24} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Unable to Load Report</h3>
        </div>
        <p style={{ fontSize: '13px', color: '#7f1d1d', margin: '0 0 16px 0' }}>{errorMsg || 'Report not found'}</p>
        <button
          type="button"
          onClick={handleBack}
          className="btn-secondary"
          style={{ width: 'auto', padding: '8px 16px', fontSize: '13px' }}
        >
          ← Return to Waiting Assessment
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '80px' }}>
      {/* 1. Header & Navigation Bar: Top row with Back Button only */}
      <div style={{ marginBottom: '16px' }}>
        <button
          type="button"
          onClick={handleBack}
          className="btn-secondary"
          style={{
            width: 'auto',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} />
          <span>Back to Waiting Assessment</span>
        </button>

        <button
          type="button"
          onClick={handleOpenMailModal}
          className="btn-secondary"
          style={{
            width: 'auto',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: '#eff6ff',
            border: '1px solid #93c5fd',
            color: '#1d4ed8',
            borderRadius: '6px',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(37, 99, 235, 0.08)',
          }}
          title="Send direct email feedback, inquiry, or update to the reporter"
        >
          <Mail size={15} />
          <span>Mail to Reporter</span>
        </button>
      </div>

      {/* 2. Top Banner Card: Report Overview & Metadata in distinct row below */}
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '20px 24px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ flex: '1 1 500px', minWidth: '280px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                SMS Assessment Desk
              </span>
              <span style={{ color: '#94a3b8' }}>•</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>
                {report.report_number}
              </span>
              <span
                style={{
                  background: '#fef3c7',
                  color: '#b45309',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '999px',
                }}
              >
                Waiting Assessment
              </span>
            </div>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>
              {report.title}
            </h2>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap',
              background: '#f8fafc',
              padding: '12px 18px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>Submitted Date</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                {report.event_date ? new Date(report.event_date).toLocaleString() : new Date(report.created_at).toLocaleString()}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #cbd5e1', paddingLeft: '16px' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>Reporter</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span>{report.reporter_identity?.full_name || report.created_by || 'Frontline Reporter'}</span>
                {report.reporter_identity?.email && (
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>({report.reporter_identity.email})</span>
                )}
                <button
                  type="button"
                  onClick={handleOpenMailModal}
                  style={{
                    padding: '2px 8px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#2563eb',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                  title="Send Direct Email to Reporter"
                >
                  <Mail size={12} />
                  <span>Mail</span>
                </button>
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #cbd5e1', paddingLeft: '16px' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>Category / Kind</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#2563eb' }}>
                {report.report_category || 'SAFETY'} • {report.report_type || 'OCCURRENCE'}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #cbd5e1', paddingLeft: '16px' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>Initial Severity</div>
              <div>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 700,
                    background: report.initial_severity === 'CRITICAL' ? '#fee2e2' : report.initial_severity === 'HIGH' ? '#ffedd5' : report.initial_severity === 'MEDIUM' ? '#fef3c7' : '#ecfdf5',
                    color: report.initial_severity === 'CRITICAL' ? '#991b1b' : report.initial_severity === 'HIGH' ? '#c2410c' : report.initial_severity === 'MEDIUM' ? '#b45309' : '#047857',
                  }}
                >
                  {report.initial_severity || 'MEDIUM'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSaveAssessment} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* 2. Top Section: Reporter's Kind of Report, Taxonomy, and Operational Overview */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            overflow: 'hidden',
          }}
        >
          {/* Classification & Taxonomy Selectors Bar */}
          <div style={{ padding: '18px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Classification & Taxonomy Routing
              </h3>
              <span style={{ fontSize: '11px', color: '#64748b' }}>
                Frontline selection on left; assign verified taxonomy on right
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '14px' }}>
              {/* Reporter's Selected Kind of Report */}
              <div
                style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Reporter Kind of Report
                </span>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#1d4ed8' }}>
                  {report.report_type || 'OCCURRENCE'}
                </div>
                <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '2px' }}>
                  Category: <strong>{report.report_category || 'SAFETY'}</strong>
                </div>
              </div>

              {/* Area of Occurrence (Taxonomy) */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  Area of Occurrence *
                </label>
                <select
                  value={reviewAreaOfOccurrence}
                  onChange={(e) => {
                    const newArea = e.target.value;
                    setReviewAreaOfOccurrence(newArea);
                    const types = taxonomyService.getTypesForArea(newArea);
                    if (types.length > 0 && !types.includes(reviewTypeOfOccurrence)) {
                      setReviewTypeOfOccurrence(types[0]);
                      const classes = taxonomyService.getClassificationsForAreaAndType(newArea, types[0]);
                      if (classes.length > 0) {
                        setReviewEventClassification(classes[0]);
                      }
                    }
                  }}
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                >
                  {reviewAreaOfOccurrence && !taxonomyAreas.includes(reviewAreaOfOccurrence) && (
                    <option value={reviewAreaOfOccurrence}>{reviewAreaOfOccurrence}</option>
                  )}
                  {taxonomyAreas.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type of Occurrence (Taxonomy) */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  Type of Occurrence *
                </label>
                <select
                  value={reviewTypeOfOccurrence}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setReviewTypeOfOccurrence(newType);
                    const classes = taxonomyService.getClassificationsForAreaAndType(reviewAreaOfOccurrence, newType);
                    if (classes.length > 0 && !classes.includes(reviewEventClassification)) {
                      setReviewEventClassification(classes[0]);
                    }
                  }}
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                >
                  {reviewTypeOfOccurrence && !taxonomyTypes.includes(reviewTypeOfOccurrence) && (
                    <option value={reviewTypeOfOccurrence}>{reviewTypeOfOccurrence}</option>
                  )}
                  {taxonomyTypes.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Event Classification */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  Event Classification
                </label>
                <select
                  value={reviewEventClassification}
                  onChange={(e) => setReviewEventClassification(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                >
                  {reviewEventClassification && !taxonomyClassifications.includes(reviewEventClassification) && (
                    <option value={reviewEventClassification}>{reviewEventClassification}</option>
                  )}
                  {taxonomyClassifications.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Verified Severity */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  Verified Severity Rating
                </label>
                <select
                  value={reviewInitialSeverity}
                  onChange={(e) => setReviewInitialSeverity(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 700 }}
                >
                  <option value="LOW">LOW</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="HIGH">HIGH</option>
                  <option value="CRITICAL">CRITICAL</option>
                </select>
              </div>
            </div>
          </div>

          {/* Operational Overview Card */}
          <div style={{ padding: '16px 20px', background: '#ffffff' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '12px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ✈️ Operational Overview
            </h4>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', fontSize: '12px' }}>
              <div>
                <span style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>Flight Number</span>
                <strong style={{ color: '#0f172a', fontSize: '13px' }}>{report.flight_number || '—'}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>Aircraft Registration</span>
                <strong style={{ color: '#0f172a', fontSize: '13px' }}>{report.aircraft_registration || '—'}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>Sector / Route</span>
                <strong style={{ color: '#0f172a', fontSize: '13px' }}>
                  {report.departure_airport || 'DEP'} → {report.destination_airport || 'DEST'}
                </strong>
              </div>

              <div>
                <span style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>Location / Aerodrome</span>
                <strong style={{ color: '#0f172a', fontSize: '13px' }}>{report.event_location || report.airport_code || '—'}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>Flight Phase</span>
                <strong style={{ color: '#0f172a', fontSize: '13px' }}>{report.flight_phase || 'CRUISE'}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>Altitude / Level</span>
                <strong style={{ color: '#0f172a', fontSize: '13px' }}>
                  {report.altitude_flight_level ? `FL${report.altitude_flight_level}` : '—'}
                </strong>
              </div>

              <div>
                <span style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>Runway & Surface</span>
                <strong style={{ color: '#0f172a', fontSize: '13px' }}>
                  {report.runway_used ? `${report.runway_used} (${report.runway_condition || 'Dry'})` : '—'}
                </strong>
              </div>

              <div>
                <span style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>Meteorological Conditions</span>
                <span style={{ color: '#0f172a', fontSize: '12px' }}>
                  {report.oat_celsius !== undefined ? `${report.oat_celsius}°C • ` : ''}
                  {report.visibility_meters !== undefined ? `${report.visibility_meters}m • ` : ''}
                  {report.daylight_condition || 'Day'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Middle Section: Occurrence Information & Attachments Tabs */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            overflow: 'hidden',
          }}
        >
          {/* Tab Navigation Bar */}
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              padding: '0 16px',
            }}
          >
            <button
              type="button"
              onClick={() => setMiddleTab('occurrence')}
              style={{
                padding: '12px 18px',
                fontSize: '13px',
                fontWeight: 700,
                color: middleTab === 'occurrence' ? '#2563eb' : '#64748b',
                background: 'none',
                border: 'none',
                borderBottom: middleTab === 'occurrence' ? '3px solid #2563eb' : '3px solid transparent',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <FileText size={16} />
              <span>Occurrence Information</span>
            </button>

            <button
              type="button"
              onClick={() => setMiddleTab('attachments')}
              style={{
                padding: '12px 18px',
                fontSize: '13px',
                fontWeight: 700,
                color: middleTab === 'attachments' ? '#2563eb' : '#64748b',
                background: 'none',
                border: 'none',
                borderBottom: middleTab === 'attachments' ? '3px solid #2563eb' : '3px solid transparent',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Paperclip size={16} />
              <span>Attachments (0)</span>
            </button>
          </div>

          {/* Tab Body */}
          <div style={{ padding: '20px' }}>
            {middleTab === 'occurrence' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                    Occurrence Subject / Title
                  </span>
                  <h4 style={{ margin: '4px 0 0', fontSize: '15px', color: '#0f172a', fontWeight: 700 }}>
                    {report.title}
                  </h4>
                </div>

                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                    Detailed Event Narrative Description
                  </span>
                  <div
                    style={{
                      marginTop: '6px',
                      whiteSpace: 'pre-wrap',
                      fontSize: '13px',
                      color: '#334155',
                      lineHeight: 1.6,
                      background: '#f8fafc',
                      padding: '14px 16px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    {report.description || 'No detailed narrative provided.'}
                  </div>
                </div>

                {/* Dynamic Questionnaire Responses from Ingestion Form */}
                {report.context_data && Object.keys(report.context_data).filter(k => !k.startsWith('reporter_selected_')).length > 0 && (
                  <div>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                      Submitted Context & Operational Parameters
                    </span>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
                      {Object.entries(report.context_data)
                        .filter(([k]) => !k.startsWith('reporter_selected_'))
                        .map(([key, val]) => (
                          <div
                            key={key}
                            style={{
                              padding: '8px 12px',
                              background: '#f8fafc',
                              borderRadius: '6px',
                              border: '1px solid #e2e8f0',
                              fontSize: '12px',
                            }}
                          >
                            <span style={{ color: '#64748b', fontWeight: 600, display: 'block', fontSize: '11px' }}>
                              {key.replace(/_/g, ' ').toUpperCase()}
                            </span>
                            <strong style={{ color: '#0f172a', wordBreak: 'break-word' }}>
                              {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                            </strong>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {middleTab === 'attachments' && (
              <div style={{ padding: '36px 20px', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📎</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>Attachments & Evidence Files</div>
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: '4px 0 0' }}>
                  No external files were uploaded with this occurrence report.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 4. Lower Section: SMS Assessment (Select Assessment Outcome) */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
              3. SMS Assessment Desk
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Select the appropriate outcome pathway to route this occurrence.
            </p>
          </div>

          {/* Three Pathway Selection Cards (Requirements 1.a & 1.c) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px', marginBottom: '20px' }}>
            {/* Info Only Card */}
            <div
              onClick={() => setAssessmentOutcome('INFO_ONLY')}
              style={{
                padding: '16px',
                borderRadius: '8px',
                border: assessmentOutcome === 'INFO_ONLY' ? '2px solid #059669' : '1px solid #cbd5e1',
                background: assessmentOutcome === 'INFO_ONLY' ? '#f0fdf4' : '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '20px' }}>ℹ️</span>
                <span style={{ fontWeight: 800, fontSize: '15px', color: '#065f46' }}>Info Only</span>
              </div>
              <p style={{ margin: 0, fontSize: '12px', color: '#4b5563', lineHeight: 1.5 }}>
                Logged for statistical monitoring & trend analysis. Closed with unique INF- reference.
              </p>
            </div>

            {/* Occurrence Card (Requirement 1.a) */}
            <div
              onClick={() => setAssessmentOutcome('CORRECTIVE_ACTION')}
              style={{
                padding: '16px',
                borderRadius: '8px',
                border: assessmentOutcome === 'CORRECTIVE_ACTION' ? '2px solid #d97706' : '1px solid #cbd5e1',
                background: assessmentOutcome === 'CORRECTIVE_ACTION' ? '#fffbeb' : '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '20px' }}>⚡</span>
                <span style={{ fontWeight: 800, fontSize: '15px', color: '#92400e' }}>
                  Occurrence
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '12px', color: '#4b5563', lineHeight: 1.5 }}>
                Operational occurrence requiring CAPA (non-serious incident or accident). Creates or links to unique OCR- occurrence record.
              </p>
            </div>

            {/* Start Investigation Card (Requirement 1.a) */}
            <div
              onClick={() => setAssessmentOutcome('INVESTIGATION')}
              style={{
                padding: '16px',
                borderRadius: '8px',
                border: assessmentOutcome === 'INVESTIGATION' ? '2px solid #7e22ce' : '1px solid #cbd5e1',
                background: assessmentOutcome === 'INVESTIGATION' ? '#faf5ff' : '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '20px' }}>🔍</span>
                <span style={{ fontWeight: 800, fontSize: '15px', color: '#6b21a8' }}>
                  Start Investigation
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '12px', color: '#4b5563', lineHeight: 1.5 }}>
                Initiate formal Safety Investigation for Serious Incidents or Accidents with INV- case reference.
              </p>
            </div>
          </div>

          {/* Dynamic Configuration Panel for Chosen Outcome */}
          {assessmentOutcome === 'INFO_ONLY' && (
            <div style={{ padding: '18px 20px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', color: '#047857', fontWeight: 700, fontSize: '14px' }}>
                <span>ℹ️ Info Only Resolution</span>
              </div>

              {/* Mode Selection: Create New vs Link to Existing (Requirement 1.c) */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setInfoLinkMode('CREATE_NEW')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: infoLinkMode === 'CREATE_NEW' ? '2px solid #059669' : '1px solid #cbd5e1',
                    background: infoLinkMode === 'CREATE_NEW' ? '#ecfdf5' : '#ffffff',
                    color: infoLinkMode === 'CREATE_NEW' ? '#047857' : '#475569',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span>+ Generate New Info Reference (INF-)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInfoLinkMode('LINK_EXISTING')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: infoLinkMode === 'LINK_EXISTING' ? '2px solid #059669' : '1px solid #cbd5e1',
                    background: infoLinkMode === 'LINK_EXISTING' ? '#ecfdf5' : '#ffffff',
                    color: infoLinkMode === 'LINK_EXISTING' ? '#047857' : '#475569',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Link2 size={15} />
                  <span>Link to Existing Info Reference...</span>
                </button>
              </div>

              {infoLinkMode === 'LINK_EXISTING' ? (
                <div style={{ marginBottom: '16px', padding: '14px', background: '#ffffff', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>
                    Select Existing Info Reference or Report *
                  </label>
                  <div style={{ marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="Search by reference (INF-), report number or title..."
                      value={infoSearchTerm}
                      onChange={(e) => setInfoSearchTerm(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                  </div>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                    {filteredInfoReports.length === 0 ? (
                      <div style={{ padding: '12px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                        No existing info reports found. You can switch to "Generate New Info Reference".
                      </div>
                    ) : (
                      filteredInfoReports.map((inf) => {
                        const refNum = inf.info_reference_number || inf.report_number;
                        const isSelected = selectedInfoReferenceNumber === refNum;
                        return (
                          <div
                            key={inf.id}
                            onClick={() => setSelectedInfoReferenceNumber(refNum)}
                            style={{
                              padding: '8px 12px',
                              borderBottom: '1px solid #f1f5f9',
                              cursor: 'pointer',
                              background: isSelected ? '#ecfdf5' : '#ffffff',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '13px', color: '#047857', fontFamily: 'monospace' }}>
                                {refNum}
                              </div>
                              <div style={{ fontSize: '12px', color: '#1e293b' }}>{inf.title}</div>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>Original Report: #{inf.report_number} • {new Date(inf.event_date || inf.created_at).toLocaleDateString()}</div>
                            </div>
                            {isSelected && <span style={{ color: '#059669', fontWeight: 800 }}>✓ Selected</span>}
                          </div>
                        );
                      })
                    )}
                  </div>
                  {selectedInfoReferenceNumber && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#047857', fontWeight: 600 }}>
                      Selected Reference: {selectedInfoReferenceNumber}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '14px', fontSize: '12px', color: '#065f46', background: '#f0fdf4', padding: '10px 14px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                  ℹ️ <strong>New Info Reference:</strong> A unique sequential <strong>INF-YYYY-XXXXX</strong> reference will be automatically generated upon assessment.
                </div>
              )}

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                  Assessment / Closeout Justification Notes
                </label>
                <textarea
                  value={assessmentNotes}
                  onChange={(e) => setAssessmentNotes(e.target.value)}
                  rows={3}
                  placeholder="State reason why this report is filed as info only (e.g. isolated low-risk observation, informational PIREP, general operational feedback)..."
                  style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#065f46', background: '#f0fdf4', padding: '10px 14px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                📌 <strong>Workflow Outcome:</strong> Report status will transition to <strong>CLOSED</strong>. The record is permanently retained in Safety Intelligence for trend monitoring.
              </div>
            </div>
          )}

          {assessmentOutcome === 'CORRECTIVE_ACTION' && (
            <div style={{ padding: '18px 20px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', color: '#92400e', fontWeight: 700, fontSize: '14px' }}>
                <span>⚡ Occurrence & Corrective Action Configuration</span>
              </div>

              {/* Mode Selection: Create New Occurrence vs Link to Existing (Requirement 1.c) */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setOccurrenceLinkMode('CREATE_NEW')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: occurrenceLinkMode === 'CREATE_NEW' ? '2px solid #d97706' : '1px solid #cbd5e1',
                    background: occurrenceLinkMode === 'CREATE_NEW' ? '#fef3c7' : '#ffffff',
                    color: occurrenceLinkMode === 'CREATE_NEW' ? '#92400e' : '#475569',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span>+ Create New Occurrence (OCR-)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setOccurrenceLinkMode('LINK_EXISTING')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: occurrenceLinkMode === 'LINK_EXISTING' ? '2px solid #d97706' : '1px solid #cbd5e1',
                    background: occurrenceLinkMode === 'LINK_EXISTING' ? '#fef3c7' : '#ffffff',
                    color: occurrenceLinkMode === 'LINK_EXISTING' ? '#92400e' : '#475569',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Link2 size={15} />
                  <span>Link to Existing Occurrence...</span>
                </button>
              </div>

              {occurrenceLinkMode === 'LINK_EXISTING' ? (
                <div style={{ marginBottom: '16px', padding: '14px', background: '#ffffff', borderRadius: '8px', border: '1px solid #fde68a' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>
                    Select Existing Occurrence in Register *
                  </label>
                  <div style={{ marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="Search by OCR number, title, or flight number..."
                      value={occurrenceSearchTerm}
                      onChange={(e) => setOccurrenceSearchTerm(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                  </div>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                    {filteredOccurrences.length === 0 ? (
                      <div style={{ padding: '12px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                        No occurrences found matching your search. You can switch to "Create New Occurrence".
                      </div>
                    ) : (
                      filteredOccurrences.map((occ) => {
                        const isSelected = selectedOccurrenceId === occ.id;
                        const displayNum = occ.occurrenceNumber
                          ? occ.occurrenceNumber.replace(/^OCC-/, 'OCR-')
                          : occ.id;
                        return (
                          <div
                            key={occ.id}
                            onClick={() => {
                              setSelectedOccurrenceId(occ.id);
                              setSelectedOccurrenceNumber(displayNum);
                              if (occ.correctiveActionDepartment) {
                                setActionDepartment(occ.correctiveActionDepartment);
                              }
                            }}
                            style={{
                              padding: '8px 12px',
                              borderBottom: '1px solid #f1f5f9',
                              cursor: 'pointer',
                              background: isSelected ? '#fef3c7' : '#ffffff',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontWeight: 700, fontSize: '13px', color: '#b45309', fontFamily: 'monospace' }}>
                                  {displayNum}
                                </span>
                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
                                  {occ.status} • {occ.severity}
                                </span>
                                {occ.correctiveActionNumber && (
                                  <span style={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    background: '#dcfce7',
                                    color: '#15803d',
                                    padding: '1px 6px',
                                    borderRadius: '4px',
                                    border: '1px solid #86efac',
                                    fontFamily: 'monospace',
                                  }}>
                                    CAPA: {occ.correctiveActionNumber}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: '#1e293b' }}>{occ.title}</div>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>
                                {new Date(occ.eventDate).toLocaleDateString()} {occ.flightNumber ? `• Flight ${occ.flightNumber}` : ''}
                              </div>
                            </div>
                            {isSelected && <span style={{ color: '#b45309', fontWeight: 800 }}>✓ Selected</span>}
                          </div>
                        );
                      })
                    )}
                  </div>
                  {selectedOccurrenceNumber && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#92400e', fontWeight: 600 }}>
                      Selected Occurrence: {selectedOccurrenceNumber}
                    </div>
                  )}
                  {selectedOccurrence && selectedOccurrence.correctiveActionNumber && (
                    <div style={{ marginTop: '10px', padding: '10px 12px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #86efac' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <CheckCircle2 size={15} color="#16a34a" />
                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#15803d' }}>
                          Shared Corrective Action: {selectedOccurrence.correctiveActionNumber}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#166534' }}>
                        {selectedOccurrence.correctiveActionTitle ? `${selectedOccurrence.correctiveActionTitle} • ` : ''}
                        Dept: <strong>{selectedOccurrence.correctiveActionDepartment || 'Assigned'}</strong>
                        {selectedOccurrence.correctiveActionDueDate ? ` • Due: ${new Date(selectedOccurrence.correctiveActionDueDate).toLocaleDateString()}` : ''}
                        {selectedOccurrence.correctiveActionPriority ? ` • Priority: ${selectedOccurrence.correctiveActionPriority}` : ''}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '14px', fontSize: '12px', color: '#92400e', background: '#fef3c7', padding: '10px 14px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                  ⚡ <strong>New Occurrence (OCR-):</strong> A new official Occurrence with unique <strong>OCR-YYYY-XXXXX</strong> number will be created in the Occurrences register and linked to this report.
                </div>
              )}

              {/* CAPA Action Configuration */}
              {occurrenceLinkMode === 'LINK_EXISTING' && selectedOccurrence?.correctiveActionNumber ? (
                <>
                  <div style={{
                    padding: '16px',
                    background: '#f0fdf4',
                    borderRadius: '8px',
                    border: '1.5px solid #86efac',
                    marginBottom: '14px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <CheckCircle2 size={18} color="#16a34a" />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#15803d' }}>
                        Shared Occurrence Corrective Action (CAPA)
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#166534', marginBottom: '10px', lineHeight: 1.5 }}>
                      Bu olay (<strong>{selectedOccurrence.occurrenceNumber}</strong>) için tanımlı mevcut bir Corrective Action bulunmaktadır.
                      Bu rapora ayrı bir aksiyon açılmayacak; olaydaki mevcut aksiyona bağlanacaktır:
                    </div>
                    <div style={{
                      background: '#ffffff',
                      padding: '12px 14px',
                      borderRadius: '6px',
                      border: '1px solid #bbf7d0',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '10px',
                      fontSize: '12px',
                    }}>
                      <div>
                        <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Action Number</span>
                        <strong style={{ color: '#15803d', fontFamily: 'monospace', fontSize: '13px' }}>{selectedOccurrence.correctiveActionNumber}</strong>
                      </div>
                      <div>
                        <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Responsible Department</span>
                        <strong style={{ color: '#334155' }}>{selectedOccurrence.correctiveActionDepartment || actionDepartment}</strong>
                      </div>
                      {selectedOccurrence.correctiveActionDueDate && (
                        <div>
                          <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Due Date</span>
                          <strong style={{ color: '#334155' }}>{new Date(selectedOccurrence.correctiveActionDueDate).toLocaleDateString()}</strong>
                        </div>
                      )}
                      {selectedOccurrence.correctiveActionPriority && (
                        <div>
                          <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Priority</span>
                          <strong style={{ color: '#334155' }}>{selectedOccurrence.correctiveActionPriority}</strong>
                        </div>
                      )}
                    </div>
                    {selectedOccurrence.correctiveActionTitle && (
                      <div style={{ fontSize: '12px', color: '#166534', marginTop: '8px' }}>
                        <strong>Scope / Title:</strong> {selectedOccurrence.correctiveActionTitle}
                      </div>
                    )}
                  </div>

                  {/* Occurrence Department Routing & Assignment */}
                  <div style={{
                    padding: '16px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    marginBottom: '14px',
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                      Occurrence Department Routing & Assignment
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                      ⚡ Bu departman yönlendirmesi ve atanan uzman bilgisi OCR detayındaki <strong>Assignments</strong> sekmesine yansıtılacaktır.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                          Responsible Department *
                        </label>
                        <select
                          value={actionDepartment}
                          onChange={(e) => setActionDepartment(e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                        >
                          {TARGET_DEPARTMENTS.map((dept) => (
                            <option key={dept} value={dept}>
                              {dept}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                          Assignee Name / Role
                        </label>
                        <input
                          type="text"
                          value={actionAssignee}
                          onChange={(e) => setActionAssignee(e.target.value)}
                          placeholder="e.g. Chief Pilot / Ops Manager"
                          style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {occurrenceLinkMode === 'LINK_EXISTING' && selectedOccurrence && !selectedOccurrence.correctiveActionNumber && (
                    <div style={{ marginBottom: '14px', fontSize: '12px', color: '#1e40af', background: '#eff6ff', padding: '10px 14px', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                      ℹ️ <strong>Initial Corrective Action:</strong> Seçilen olayda ({selectedOccurrence.occurrenceNumber}) henüz tanımlı bir Corrective Action bulunmadığı için aşağıda oluşturacağınız aksiyon bu olayın ortak aksiyonu olarak atanacaktır.
                    </div>
                  )}

                  {/* CAPA Action fields */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Action Name / Custom Label
                      </label>
                      <input
                        type="text"
                        value={actionCustomLabel}
                        onChange={(e) => setActionCustomLabel(e.target.value)}
                        placeholder="e.g. Corrective Action (CAPA)"
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      />
                      <div style={{ marginTop: '4px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {['Corrective Action (CAPA)', 'Preventive Action', 'Direct Safety Notice', 'Immediate Remediation'].map((label) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() => setActionCustomLabel(label)}
                            style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              border: '1px solid #cbd5e1',
                              background: actionCustomLabel === label ? '#e0e7ff' : '#ffffff',
                              color: actionCustomLabel === label ? '#3730a3' : '#475569',
                              cursor: 'pointer',
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Action Priority *
                      </label>
                      <select
                        value={actionPriority}
                        onChange={(e) => setActionPriority(e.target.value as any)}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="LOW">LOW</option>
                        <option value="MEDIUM">MEDIUM</option>
                        <option value="HIGH">HIGH</option>
                        <option value="IMMEDIATE">IMMEDIATE / URGENT</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                      Action Title / Scope *
                    </label>
                    <input
                      type="text"
                      required
                      value={actionTitle}
                      onChange={(e) => setActionTitle(e.target.value)}
                      placeholder="e.g. Flight crew recurrent briefing on gusty crosswind landing technique"
                      style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Action Type
                      </label>
                      <select
                        value={actionType}
                        onChange={(e) => setActionType(e.target.value as any)}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="CORRECTIVE">Corrective Action</option>
                        <option value="PREVENTIVE">Preventive Action</option>
                        <option value="CONTAINMENT">Containment / Immediate</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Responsible Department *
                      </label>
                      <select
                        value={actionDepartment}
                        onChange={(e) => setActionDepartment(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      >
                        {TARGET_DEPARTMENTS.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Assignee Name / Role
                      </label>
                      <input
                        type="text"
                        value={actionAssignee}
                        onChange={(e) => setActionAssignee(e.target.value)}
                        placeholder="e.g. Chief Pilot / Ops Manager"
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Action Due Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={actionDueDate}
                        onChange={(e) => setActionDueDate(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      />
                    </div>
                  </div>

                  <div style={{ fontSize: '12px', color: '#92400e', background: '#fef3c7', padding: '10px 14px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                    ⚡ <strong>Workflow Outcome:</strong> A formal Corrective Action will be generated with action number <strong>ACT-YYYY-XXXXX</strong>. Report transitions to <strong>PENDING_ACTION</strong> and is linked to Occurrence.
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#0369a1', background: '#e0f2fe', padding: '10px 14px', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                    👥 <strong>Department Assignment:</strong> Seçilen sorumlu departman ve atanan uzman bilgisi, oluşturulacak Occurrence (OCR) kaydının <strong>Assignments</strong> sekmesine otomatik olarak eklenecektir.
                  </div>
                </>
              )}
            </div>
          )}

          {assessmentOutcome === 'INVESTIGATION' && (
            <div style={{ padding: '18px 20px', background: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', color: '#6b21a8', fontWeight: 700, fontSize: '14px' }}>
                <span>🔍 Safety Investigation Configuration (ICAO Annex 13)</span>
              </div>

              {/* Mode Selection: Launch New Investigation vs Link to Existing (Requirement 1.c) */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setInvestigationLinkMode('CREATE_NEW')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: investigationLinkMode === 'CREATE_NEW' ? '2px solid #7e22ce' : '1px solid #cbd5e1',
                    background: investigationLinkMode === 'CREATE_NEW' ? '#f3e8ff' : '#ffffff',
                    color: investigationLinkMode === 'CREATE_NEW' ? '#6b21a8' : '#475569',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span>+ Launch New Investigation (INV-)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInvestigationLinkMode('LINK_EXISTING')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: investigationLinkMode === 'LINK_EXISTING' ? '2px solid #7e22ce' : '1px solid #cbd5e1',
                    background: investigationLinkMode === 'LINK_EXISTING' ? '#f3e8ff' : '#ffffff',
                    color: investigationLinkMode === 'LINK_EXISTING' ? '#6b21a8' : '#475569',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Link2 size={15} />
                  <span>Link to Existing Investigation...</span>
                </button>
              </div>

              {investigationLinkMode === 'LINK_EXISTING' ? (
                <div style={{ marginBottom: '16px', padding: '14px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>
                    Select Existing Safety Investigation *
                  </label>
                  <div style={{ marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="Search by INV number or title..."
                      value={investigationSearchTerm}
                      onChange={(e) => setInvestigationSearchTerm(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                  </div>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                    {filteredInvestigations.length === 0 ? (
                      <div style={{ padding: '12px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                        No investigations found matching your search. You can switch to "Launch New Investigation".
                      </div>
                    ) : (
                      filteredInvestigations.map((inv) => {
                        const isSelected = selectedInvestigationId === inv.id;
                        return (
                          <div
                            key={inv.id}
                            onClick={() => {
                              setSelectedInvestigationId(inv.id);
                              setSelectedInvestigationNumber(inv.investigationNumber);
                            }}
                            style={{
                              padding: '8px 12px',
                              borderBottom: '1px solid #f1f5f9',
                              cursor: 'pointer',
                              background: isSelected ? '#f3e8ff' : '#ffffff',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontWeight: 700, fontSize: '13px', color: '#6b21a8', fontFamily: 'monospace' }}>
                                  {inv.investigationNumber}
                                </span>
                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
                                  {inv.status} • {inv.priority}
                                </span>
                              </div>
                              <div style={{ fontSize: '12px', color: '#1e293b' }}>{inv.title}</div>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>Type: {inv.type}</div>
                            </div>
                            {isSelected && <span style={{ color: '#6b21a8', fontWeight: 800 }}>✓ Selected</span>}
                          </div>
                        );
                      })
                    )}
                  </div>
                  {selectedInvestigationNumber && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b21a8', fontWeight: 600 }}>
                      Selected Investigation: {selectedInvestigationNumber}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Investigation Case Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={investigationTitle}
                        onChange={(e) => setInvestigationTitle(e.target.value)}
                        placeholder="e.g. Investigation of Flight Controls Discrepancy on Approach"
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Investigation Framework Type
                      </label>
                      <select
                        value={investigationType}
                        onChange={(e) => setInvestigationType(e.target.value as any)}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="FORMAL">FORMAL (Full ICAO Annex 13 Board)</option>
                        <option value="COMPREHENSIVE">COMPREHENSIVE (Root Cause Analysis)</option>
                        <option value="PRELIMINARY">PRELIMINARY (Quick Fact-Finding)</option>
                        <option value="DESK">DESK (Technical Analysis)</option>
                        <option value="SPECIAL">SPECIAL DIRECTIVE</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Investigation Priority
                      </label>
                      <select
                        value={investigationPriority}
                        onChange={(e) => setInvestigationPriority(e.target.value as any)}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="CRITICAL">CRITICAL (Accident / Immediate Board)</option>
                        <option value="HIGH">HIGH (Serious Incident)</option>
                        <option value="MEDIUM">MEDIUM (Operational Risk Event)</option>
                        <option value="LOW">LOW</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Lead Investigator Name
                      </label>
                      <input
                        type="text"
                        value={leadInvestigator}
                        onChange={(e) => setLeadInvestigator(e.target.value)}
                        placeholder="e.g. Capt. Erdal Aydeniz (Lead Safety Investigator)"
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Target Completion Date
                      </label>
                      <input
                        type="date"
                        value={targetCompletionDate}
                        onChange={(e) => setTargetCompletionDate(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                      Investigation Summary & Scope
                    </label>
                    <textarea
                      value={investigationSummary}
                      onChange={(e) => setInvestigationSummary(e.target.value)}
                      rows={3}
                      placeholder="Outline investigation terms of reference, initial facts, and preliminary focus areas..."
                      style={{ width: '100%', padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                  </div>
                </>
              )}

              <div style={{ fontSize: '12px', color: '#6b21a8', background: '#f3e8ff', padding: '10px 14px', borderRadius: '6px', border: '1px solid #d8b4fe' }}>
                🔍 <strong>Workflow Outcome:</strong> Safety Investigation will be linked with unique sequential case number <strong>INV-YYYY-XXXXX</strong>. Dossier will be formatted under ICAO Annex 13 standards and accessible in Safety Investigations.
              </div>
            </div>
          )}
        </div>

        {/* 5. Bottom Bar: Delete Report, Reject Report, Save Draft, Save Assessment */}
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.05)',
            zIndex: 100,
            borderRadius: '0 0 10px 10px',
          }}
        >
          {/* Left Actions: Delete & Reject */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              disabled={isSaving}
              onClick={() => {
                setDeleteReason('');
                setShowDeleteModal(true);
              }}
              style={{
                background: '#fee2e2',
                color: '#b91c1c',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              <Trash2 size={16} />
              <span>Delete Report</span>
            </button>

            <button
              type="button"
              disabled={isSaving}
              onClick={() => {
                setRejectReason('');
                setShowRejectModal(true);
              }}
              style={{
                background: '#ffedd5',
                color: '#c2410c',
                border: '1px solid #fdba74',
                borderRadius: '6px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              <XCircle size={16} />
              <span>Reject Report</span>
            </button>
          </div>

          {/* Right Actions: Save Draft & Save Assessment */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              type="button"
              disabled={isSaving}
              onClick={handleSaveValidationDraft}
              className="btn-secondary"
              style={{
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              <FileText size={16} />
              <span>{isSaving ? 'Saving Draft...' : 'Save Draft'}</span>
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="btn-primary"
              style={{
                padding: '10px 28px',
                fontSize: '14px',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.25)',
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              <Save size={18} />
              <span>{isSaving ? 'Saving Assessment...' : 'Save Assessment'}</span>
            </button>
          </div>
        </div>
      </form>

      {/* Delete Report Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ borderBottom: '1px solid #fee2e2', background: '#fef2f2' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>⚠️</span> Soft-Delete Report #{report?.report_number}
                </h3>
                <p style={{ fontSize: '12px', color: '#991b1b', margin: '2px 0 0' }}>
                  Aviation Audit Governance: Physical deletion is prohibited; record will be archived.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#991b1b' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <div
                style={{
                  padding: '12px 14px',
                  background: '#fff1f2',
                  border: '1px solid #fecdd3',
                  borderRadius: '6px',
                  marginBottom: '14px',
                  fontSize: '12px',
                  color: '#9f1239',
                  lineHeight: 1.5,
                }}
              >
                <strong>⚠️ Warning:</strong> You are about to remove this report from active intake queues. Under SafetyCore Compliance & Audit rules, this action will be permanently recorded in the immutable audit trail along with your identity and mandatory reason.
              </div>

              <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>
                Reason for Deletion * (Mandatory)
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={4}
                required
                className="form-control"
                placeholder="State why this report is being deleted (e.g. duplicate submission, accidental test record, invalid entry)..."
                style={{ fontSize: '13px', width: '100%' }}
              />
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '16px 20px', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="btn-secondary"
                style={{ width: 'auto', padding: '8px 16px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isSaving || deleteReason.trim().length < 3}
                style={{
                  background: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: deleteReason.trim().length < 3 ? 'not-allowed' : 'pointer',
                  opacity: deleteReason.trim().length < 3 ? 0.6 : 1,
                }}
              >
                {isSaving ? 'Deleting...' : 'Confirm Soft-Delete & Log Audit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Report Confirmation Modal */}
      {showRejectModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#b91c1c' }}>
                  Reject Report #{report?.report_number}
                </h3>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                  State the formal reason for rejection. This will be recorded in the audit trail.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>
                Rejection Reason *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                required
                className="form-control"
                placeholder="State why this report cannot be validated (e.g. invalid submission, duplicate occurrence, false alarm)..."
                style={{ fontSize: '13px', width: '100%' }}
              />
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '16px 20px', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                className="btn-secondary"
                style={{ width: 'auto', padding: '8px 16px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReject}
                disabled={isSaving || !rejectReason.trim()}
                style={{
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: !rejectReason.trim() ? 'not-allowed' : 'pointer',
                  opacity: !rejectReason.trim() ? 0.6 : 1,
                }}
              >
                {isSaving ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Mail to Reporter Modal */}
      {showMailModal && report && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: '16px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSendingMail) {
              setShowMailModal(false);
            }
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              maxWidth: '680px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '18px 24px',
                borderBottom: '1px solid #e2e8f0',
                background: '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#1d4ed8',
                  }}
                >
                  <Mail size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                    Mail to Reporter
                  </h3>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    Report #{report.report_number} • {report.title}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !isSendingMail && setShowMailModal(false)}
                disabled={isSendingMail}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: isSendingMail ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSendMailToReporter} style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {/* Notice Banner */}
              <div
                style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#1e40af',
                  lineHeight: 1.5,
                }}
              >
                Dispatches an official email to the reporter via the SMS Notification Gateway. The message is permanently recorded in the report timeline, audit trail, and communication history.
              </div>

              {/* Status Message */}
              {mailStatusMsg && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    background: mailStatusMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${mailStatusMsg.type === 'success' ? '#86efac' : '#fca5a5'}`,
                    color: mailStatusMsg.type === 'success' ? '#166534' : '#991b1b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {mailStatusMsg.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                  <span>{mailStatusMsg.text}</span>
                </div>
              )}

              {/* Recipient */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Reporter Email Address <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="email"
                  value={mailRecipient}
                  onChange={(e) => setMailRecipient(e.target.value)}
                  placeholder="reporter@example.com"
                  required
                  className="form-control"
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                  }}
                />
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Subject Line <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                  placeholder="Email subject..."
                  required
                  className="form-control"
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                  }}
                />
              </div>

              {/* Template Quick Selection */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Quick Response Templates:
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => handleApplyMailTemplate('DETAILS')}
                    className="btn-secondary"
                    style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '4px', background: '#f8fafc' }}
                  >
                    Request Details
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMailTemplate('ASSESSMENT')}
                    className="btn-secondary"
                    style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '4px', background: '#f8fafc' }}
                  >
                    Under Assessment
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMailTemplate('ACTION')}
                    className="btn-secondary"
                    style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '4px', background: '#f8fafc' }}
                  >
                    Occurrence / Action Opened
                  </button>
                </div>
              </div>

              {/* Message Body */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Message Content <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  value={mailMessage}
                  onChange={(e) => setMailMessage(e.target.value)}
                  placeholder="Write your direct response or question to the reporter here..."
                  rows={6}
                  required
                  className="form-control"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    lineHeight: 1.5,
                  }}
                />
              </div>

              {/* Modal Footer */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  paddingTop: '14px',
                  borderTop: '1px solid #e2e8f0',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowMailModal(false)}
                  disabled={isSendingMail}
                  className="btn-secondary"
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    borderRadius: '6px',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSendingMail || !mailRecipient.trim() || !mailMessage.trim()}
                  style={{
                    background: '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 20px',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: (isSendingMail || !mailRecipient.trim() || !mailMessage.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (isSendingMail || !mailRecipient.trim() || !mailMessage.trim()) ? 0.6 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Send size={14} />
                  <span>{isSendingMail ? 'Sending Email...' : 'Send Email & Log'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
